import { useEffect, useState } from 'react'
import { useScriptLoader } from 'hooks'
import logger from 'logger'
import config from 'config'


const scriptUrl = config.amazonPayUrl || 'https://static-na.payments-amazon.com/OffAmazonPayments/us/js/Widgets.js'

type Result = {
  isFetching: boolean
  isLoaded: boolean
  error: Error
}

const useAmazonPay = (): Result => {
  const [ readyState, setReadyState ] = useState({ isLoaded: false, error: null })
  const scriptState = useScriptLoader(scriptUrl)

  useEffect(() => {
    if (window.amazon && window.OffAmazonPayments) {
      setReadyState({ isLoaded: true, error: null })
      return
    }

    let loginReady = false
    let paymentsReady = false
    let timeout = null

    const finalCheck = (attempt = 0) => {
      if (window.amazon && window.OffAmazonPayments) {
        setReadyState({ isLoaded: true, error: null })
      }
      else if (attempt < 10) {
        timeout = setTimeout(() => finalCheck(++attempt), 300)
      }
      else {
        const error = 'Attempted to load Amazon script. Timeout!'

        logger.error({
          error,
          scriptState,
          'window.amazon': Boolean(window.amazon),
          'window.OffAmazonPayments': Boolean(window.OffAmazonPayments),
        })

        setReadyState({ isLoaded: false, error })
      }
    }

    const checkReady = () => {
      if (loginReady && paymentsReady) {
        finalCheck()
      }
    }

    window.onAmazonLoginReady = () => {
      loginReady = true
      checkReady()
    }

    window.onAmazonPaymentsReady = () => {
      paymentsReady = true
      checkReady()
    }

    return () => {
      window.onAmazonLoginReady = null
      window.onAmazonPaymentsReady = null
      clearTimeout(timeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isLoaded = scriptState.isScriptLoaded && readyState.isLoaded
  const error = scriptState.scriptLoadingError || readyState.error
  const isFetching = Boolean(!isLoaded || error)

  return {
    isFetching,
    isLoaded,
    error,
  }
}


export default useAmazonPay
